/**
 * auth-context.js
 */

import React from "react";
import axios from 'axios';
import {
  useMediaQuery,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { useHistory, useLocation, Link } from "react-router-dom";
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { isValidEmail } from 'utils/user-profile-validation';
import { UserContext } from 'contexts/user/user-context';
import { AppMessagingContext } from "contexts/app-messaging/app-messaging-context";
import { AppUtilityContext } from "contexts/app-utility/app-utility-context";
import useRecordStore from "contexts/record-store";
import FeatureBox from "components/feature-box";
import { anonymousRoutes } from "routing/public-router/public-router";
import themes from 'themes/themes';
import logoBadge from 'assets/images/logo-badge-login.png';


export const AuthContext = React.createContext();
const theme = themes[Types.Theme.LIGHT];


const AuthTextField = (props) => {
  return (
    <TextField
      {...props}
      InputLabelProps={{ shrink: true }}
      sx={{ 
        '& .MuiInputBase-root': {
          backgroundColor: theme.palette.sys.surface,
          color: theme.palette.sys.onSurface,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.sys.onSurface,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.sys.primary
          }
        },
        '& .MuiInputBase-input:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0 30px ${theme.palette.sys.surface} inset !important`,
          WebkitTextFillColor: `${theme.palette.sys.onSurface} !important`
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(0, 0, 0, 0.23)'
        },
        '& .MuiFormLabel-root': {
          color: theme.palette.sys.onSurface
        },
        ...props.sx,
      }}
    />
  );
}


const PrimaryButton = (props) => {
  return (
    <Button
      {...props}
      variant='contained'
      sx={{
        backgroundColor: theme.palette.sys.secondary,
        color: theme.palette.sys.onSecondary,
        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
        ...props.sx
      }}
    />
  );
}


const SecondaryButton = (props) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  return (
    <Button
      {...props}
      variant={isMobile ? 'contained' : 'text'}
      sx={{
        backgroundColor: isMobile ? theme.palette.grey[100] : 'auto',
        color: theme.palette.sys.primary,
        textTransform: 'none',
        ...props.sx,
        '&:hover': {
          backgroundColor: isMobile ? theme.palette.grey[200] : 'auto'
        }
      }}
    />
  );
}


const AuthLink = (props) => {
  return (
    <MuiLink
      {...props}
      sx={{
        color: theme.palette.sys.primary,
        '&:hover': {
          color: theme.palette.primary.dark,
        }
      }}
    />
  )
}


const ContentText = (props) => {
  return (
    <Typography 
      variant='h2' 
      sx={{ 
        py: 2, 
        pt: 0,
        px: 1,
        mb: 2,
        fontSize: 'sizes.2', 
        fontWeight: 400, 
        textAlign: 'center',
      }}
    >
      {props.children}
    </Typography>
  );
}


const AuthDialogBase = (props) => {
  const { isOpen, isLoading, title, content, actions } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { user } = React.useContext(UserContext);
  const padding = isMobile ? 24 : 48;


  return (
    <Dialog 
      open={isOpen} 
      aria-labelledby="form-dialog-title"
      sx={{ 
        borderRadius: 12,
        '& .MuiBackdrop-root': {
          backgroundColor: user ? theme.palette.action.active : 'transparent',
        },
        '& .MuiDialog-container': {
          overflowY: 'scroll',
        },
        '& .MuiPaper-root': { 
          maxWidth: '480px',
          backgroundColor: theme.palette.sys.surface,
          overflow: 'visible',
          maxHeight: 'none',
          color: theme.palette.sys.onSurface,
          borderRadius: '12px',
          boxShadow: `
            0px 10px 13px -6px hsla(210, 24%, 16%, 0.6),
            0px 20px 31px 3px hsla(210, 24%, 16%, 0.54),
            0px 8px 38px 7px hsla(210, 24%, 16%, 0.52)`,
        } 
      }}
    >
      <DialogTitle 
        id="form-dialog-title" 
        sx={{
          backgroundColor: 'transparent',
          color: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          fontSize: 'sizes.6',
          fontWeight: 'bold',
          px: `${padding}px !important`,
        }}
      >
        <Box sx={{
          mt: isMobile ? '-50px' : '-75px',
          mb: isMobile ? 0 : 1,
          '& img': { height: isMobile ? '100px' : '160px' }
        }}> 
          <img src={logoBadge} alt='PACKTRACK logo' />
        </Box>
        <span>{title}</span>
      </DialogTitle>
      <FeatureBox loading={isLoading} spinnerSize={60} spinnerTop={-20}>
        <DialogContent sx={{ p: `0 ${padding}px 20px` }}>
          {content}
        </DialogContent>
        {actions &&
        <DialogActions sx={{ p: `0 ${padding}px ${padding}px` }}>
          {actions}
        </DialogActions>}
      </FeatureBox>
    </Dialog>
  )
}


const AuthDialog = (props) => {
  const {
    isOpen,
    setIsAuthenticated,
    setIsForgotPasswordOpen,
    showPersistentError,
    dismissError,
  } = props;
  const history = useHistory();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { user } = React.useContext(UserContext);
  const { alertInfo } = React.useContext(AppUtilityContext);
  const { hasPasscode, passcodeLogin, setPasscodeToken } = React.useContext(AppMessagingContext);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPasscodeForm, setShowPasscodeForm] = React.useState();
  const [passcode, setPasscode] = React.useState('');
  const [updatedToken, setUpdatedToken] = React.useState();
  const [isLoading, setIsLoading] = React.useState(false);


  React.useEffect(() => {
    setShowPasscodeForm(hasPasscode);
  }, [hasPasscode]);


  // after a mobile login (which returns an updated passcode token)
  // and the user object is updated, notify the app of the new token.
  React.useEffect(() => {
    (async() => {
      if (user && updatedToken) {
        await setPasscodeToken(updatedToken);
        setUpdatedToken();
      }
    })();
  }, [updatedToken, user, setPasscodeToken]);


  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      login();
    }
  }


  const login = async () => {
    if (!username) {
      showPersistentError('Please enter a username');
      return;
    } else if (!password) {
      showPersistentError('Please enter a password');
      return;
    }

    try {
      setIsLoading(true);
      dismissError();
      const response = await axios.post(
        '/account/login', 
        { username, password, isMobile },
        { bypassErrorHandling: true });
      setIsAuthenticated(response.status === 200);
      if (hasPasscode && response.isSuccess && response.data?.passcodeToken) {
        setUpdatedToken(response.data.passcodeToken);
        setShowPasscodeForm(true);
      }
    } catch (e) {
      let message = e.response?.data?.Message;
      if (!message) {
        message = e.response?.data;
      }
      if (typeof message !== 'string') {
        message = 'Error logging in'
      }
      showPersistentError(message);
    } finally {
      setIsLoading(false);
    }
  }


  const onSetPasscode = async (value) => {
    setPasscode(value);
    if (value.length === 4) {
      dismissError();
      const response = await passcodeLogin(value);
      if (response?.value) {
        try {
          setIsLoading(true);
          const loginResponse = await axios.post(
            `/account/token-login/${response.userId}/${response.token}`,
            { bypassErrorHandling: true });
          if (loginResponse.isSuccess) {
            setPasscode('');
            history.push('/home');
          } else {
            throw new Error(loginResponse.status);
          }
        } catch (e) {
          const reason = e.message === '401'
            ? 'Your passcode token has expired. Please login with your username and password to refresh it.'
            : e.message === '429'
              ? 'Too many requests'
              : 'Unknown passcode error. Please login with your username and password.';
          setPasscode('');
          showPersistentError(reason);
          setShowPasscodeForm(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        const reason = !response
          ? 'No passcode set'
          : response.reason;
        setPasscode('');
        showPersistentError(reason);
        if (response.id === 'token-expired' || response.id === 'max-retries') {
          setShowPasscodeForm(false);
        }
      }
    }
  }


  return (
    <AuthDialogBase
      isOpen={isOpen}
      isLoading={isLoading}
      title={<Box sx={{ display: 'flex', alignItems: 'baseline' }}>
        <Box 
          sx={{ 
            fontFamily: 'good_timing_bd',
            fontSize: isMobile ? '7.5vw' : '45px',
            marginRight: theme => theme.spacing(1.5),
          }}
          children='PACKTRACK'
        />
        LOGIN
      </Box>}

      content={<>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {!isMobile && 
          <>
            <ContentText>
              Welcome to <AuthLink href='https://packtrackapp.com'><b>PACKTRACK</b></AuthLink>, 
              The #1 online K9 record keeping system! 
              Login below to create K9 records and reports on your training and deployment activities.
              You can also install the mobile app for <></>
              <AuthLink href='https://itunes.apple.com/us/app/packtrack-app/id491075340?mt=8'>iPhone</AuthLink> <></>
              and <></>
              <AuthLink href='https://play.google.com/store/apps/details?id=com.csnmedia.android.packtrack'>Android</AuthLink>
            </ContentText>
          </>}
        </Box>

        {!showPasscodeForm &&
        <>
          <AuthTextField
            id="username"
            data-testid="username-field"
            label="Email Address"
            margin="normal"
            fullWidth
            sx={{ marginBottom: isMobile ? 0 : 1 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'username-input' }}
            autoFocus
            value={username}
            onChange={e => setUsername(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <AuthTextField
            id="password"
            data-testid="password-field"
            label="Password"
            type="password"
            autoComplete="current-password"
            margin="normal"
            fullWidth
            sx={{ marginBottom: isMobile ? 0 : 1 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'password-input' }}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </>}

        {showPasscodeForm &&
        <>
          <AuthTextField
            label='Passcode'
            placeholder='Enter Passcode'
            type='tel'
            inputProps={{ 
              pattern: '[0-9]*', 
              style: { 
                WebkitTextSecurity: 'disc',
                height: '26px',
                fontSize: Boolean(passcode) ? '40px' : '20px',
                textAlign: Boolean(passcode) ? 'center' : 'left',
                letterSpacing: Boolean(passcode) ? '8px' : '1px'
              }
            }}
            inputMode='tel'
            margin='normal'
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={passcode}
            onChange={e => onSetPasscode(e.target.value)}
          />
          <SecondaryButton
            fullWidth
            sx={{ textTransform: 'none', mt: 3 }}
            onClick={() => setShowPasscodeForm(false)}
            children='Login with Email Instead'
          />
        </>}
      </>}

      actions={!showPasscodeForm && <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <PrimaryButton 
          data-testid='login-btn'
          fullWidth
          sx={{ py: 1.5 }}
          onClick={login}
          children='Login'
        />
        <SecondaryButton
          sx={{ mt: 4 }}
          onClick={() => setIsForgotPasswordOpen(true)}
          children='Forgot Password?'
        />
        {isMobile && 
        <Typography variant='body1' sx={{ textAlign: 'center', mt: 2 }}>
          <SecondaryButton 
            component={Link} 
            sx={{ width: '100%' }}
            to='/tracking'
            children={<>Skip Login - Track Layers Only</>}
          />
        </Typography>}
        {isMobile && 
        <SecondaryButton
          sx={{ mt: 2 }}
          children='Learn More'
          onClick={() => alertInfo({ 
            title: 'Subscription Required',
            text: <>
              This app requires a subscription to the <AuthLink href='https://packtrackapp.com'>PACKTRACK</AuthLink> service.<br/><br/>
              Ensure that your <AuthLink href='/get-started'>REGISTRATION</AuthLink> is complete before logging into the mobile app.
            </>
          })}
        />}
      </Box>}
    />
  );
}


const ForgotPasswordDialog = (props) => {
  const {
    isOpen,
    setIsForgotPasswordOpen,
    showPersistentError,
    dismissError,
  } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);


  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      onSubmit();
    }
  }


  const onSubmit = async () => {
    try {
      setIsLoading(true);
      dismissError();
      const response = await axios.post('/account/forgot-password', { email }, { bypassErrorHandling: true });
      if (response.isSuccess) {
        enqueueSnackbar(`Request submitted`, { variant: 'success' });
        setIsForgotPasswordOpen(false);
        setEmail('');
      }
    } catch (e) {
      showPersistentError(e.response?.data?.Message ?? 'Error submitting password reset request.');
    } finally {
      setIsLoading(false);
    }
  }


  return (
    <AuthDialogBase
      isOpen={isOpen}
      isLoading={isLoading}
      title={<Box sx={{ 
        fontSize: isMobile ? 'sizes.7' : 'sizes.9',
        mt: isMobile ? 1 : 0,
      }}>Forgot Password</Box>}

      content={<>
        <ContentText>
          Type your email address below. 
          If you're a PACKTRACK member then we'll email you a link that you can use to reset your password. 
          If you don't receive the link then please contact us.
        </ContentText>
        <AuthTextField
          label="Email Address"
          margin="normal"
          fullWidth
          value={email}
          autoFocus
          sx={{ mb: 2 }}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
          inputProps={{ 'data-testid': 'username-input' }}
        />
      </>}

      actions={<>
        <SecondaryButton
          onClick={() => {
            setEmail('');
            setIsForgotPasswordOpen(false);
          }}
          children='Cancel'
        />
        <PrimaryButton 
          data-testid='login-btn'
          disabled={!isValidEmail(email)}
          onClick={onSubmit}
          children='Submit'
        />
      </>}
    />
  );
}


export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { setUser } = React.useContext(UserContext);
  const recordStore = useRecordStore();
  const [state, setState] = React.useState({ isAuthenticated: null });
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = React.useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = React.useState(false);
  const notificationRef = React.useRef();
  // if we are on an anonymous route then don't show the auth dialog
  const isRouteAnonymous = React.useMemo(() => {
    return anonymousRoutes
      .filter(r => location.pathname.startsWith(r))
      .length > 0;
  }, [location.pathname]);


  const setIsAuthenticated = React.useCallback((isAuthenticated) => {
    setState(p => ({
      ...p,
      isAuthenticated
    }));
  }, []);


  const logout = React.useCallback(async () => {
    try {
      closeSnackbar();
      setIsLogoutLoading(true);
      const response = await axios.post('/account/logout');
      if (response.isSuccess) {
        setUser(null);
        setState(p => ({ ...p, isAuthenticated: false }));
        recordStore.reset();
        history.push('/');
      }
    } finally {
      setIsLogoutLoading(false);
    }
  // recordStore is complex object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, setUser, closeSnackbar]);


  const showPersistentError = (message) => {
    closeSnackbar(notificationRef.current);
    notificationRef.current = enqueueSnackbar(message, {
      variant: 'error',
      persist: true,
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      }});
  }


  const dismissError = () => {
    closeSnackbar(notificationRef.current);
  }


  // final state value
  const value = React.useMemo(() => ({
    ...state,
    setIsAuthenticated,
    setIsForgotPasswordOpen,
    logout,
  }), [
    state,
    setIsAuthenticated,
    setIsForgotPasswordOpen,
    logout,
  ]);


  return (
    <>
      <AuthContext.Provider value={value}>
        {children}

        <AuthDialog
          isOpen={state.isAuthenticated === false && !isForgotPasswordOpen && !isRouteAnonymous}
          setIsAuthenticated={isAuthenticated => setState(p => ({ ...p, isAuthenticated }))}
          setIsForgotPasswordOpen={setIsForgotPasswordOpen}
          showPersistentError={showPersistentError}
          dismissError={dismissError}
        />

        <ForgotPasswordDialog
          isOpen={isForgotPasswordOpen}
          setIsForgotPasswordOpen={setIsForgotPasswordOpen}
          showPersistentError={showPersistentError}
          dismissError={dismissError}
        />

        {isLogoutLoading &&
        <FeatureBox 
          fadeBackground
          spinnerSize={70}
          sx={{
            position: 'fixed !important',
            inset: 0,
            height: '100vh',
            width: '100vw',
            zIndex: 10000,
          }}
          loading={isLogoutLoading} 
        />}
      </AuthContext.Provider>
    </>
  );
};